import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "./ui/dialog";
import { InquiryStepper } from "./InquiryStepper";
import { track } from "../analytics/sdk";

const Ctx = createContext({ open: () => {}, close: () => {} });
export const usePlanEvent = () => useContext(Ctx);

export const PlanEventProvider = ({ children }) => {
  const [state, setState] = useState({ open: false, eventType: "" });
  const opener = useRef(null);
  const { pathname, search } = useLocation();

  const open = useCallback((eventType = "", label) => {
    opener.current = document.activeElement;
    setState({ open: true, eventType });
    track("modal_open", { name: "plan_your_event", label, event_type: eventType || undefined });
  }, []);
  const close = useCallback(() => setState((s) => ({ ...s, open: false })), []);

  useEffect(() => {
    if (new URLSearchParams(search).get("plan") === "1" && pathname !== "/contact") open();
  }, [pathname, search, open]);

  const value = useMemo(() => ({ open, close }), [open, close]);
  return (
    <Ctx.Provider value={value}>
      {children}
      <Dialog open={state.open} onOpenChange={(o) => !o && close()}>
        <DialogContent data-testid="plan-event-modal" onCloseAutoFocus={(e) => { e.preventDefault(); opener.current?.focus?.(); }} className="inset-0 left-0 top-0 h-full w-full max-w-none translate-x-0 translate-y-0 gap-0 overflow-y-auto rounded-none border-0 bg-white p-0 text-black sm:inset-auto sm:left-1/2 sm:top-1/2 sm:h-auto sm:max-h-[90vh] sm:w-[calc(100%-2rem)] sm:max-w-[640px] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-3xl sm:shadow-[0_40px_80px_-30px_rgba(0,0,0,0.5)]">
          <div className="h-1 w-full bg-en-yellow" aria-hidden />
          <div className="px-5 pb-8 pt-7 sm:px-8">
            <p className="eyebrow mb-2">Let's talk</p>
            <DialogTitle className="font-display text-3xl leading-tight text-black sm:text-4xl">Plan Your Event</DialogTitle>
            <DialogDescription className="mt-2 text-base text-black/60">Tell us a little about what you're planning and we'll take it from there.</DialogDescription>
            <div className="mt-7">
              <InquiryStepper key={`${state.open}-${state.eventType}`} presetEventType={state.eventType} onClose={close} />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Ctx.Provider>
  );
};

export const InquiryCTA = ({ label = "Plan Your Event", eventType = "", icon = false, onClick, className, ...rest }) => {
  const { open } = usePlanEvent();
  const handle = (e) => {
    onClick?.(e);
    if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    e.currentTarget.focus();
    open(eventType, label);
  };
  return (
    <Link to="/contact" onClick={handle} data-track="plan_your_event" data-track-label={label} className={className} {...rest}>
      {label}{icon && <ArrowRight className="h-4 w-4" />}
    </Link>
  );
};
